<template lang="pug">
  table.table.table--bars
    thead
      tr.table__row.table__row--head
        th.table__cell {{ 'base.date' | translate }}
        th.table__cell {{ 'base.status' | translate }}
        th.table__cell {{ 'base.period' | translate }}
        th.table__cell {{ 'base.loading' | translate }}
        th.table__cell {{ 'base.ration' | translate }}
        th.table__cell {{ 'base.animal_groups' | translate }}
        th.table__cell {{ 'base.animal_cnt' | translate }}
        th.table__cell {{ 'base.starting_mass' | translate }}
        th.table__cell {{ 'base.weight' | translate }} ({{ 'base.kg' | translate }})#[br] {{ 'base.plan_fact' | translate }}
        th.table__cell {{ 'base.fresh_weight' | translate }}#[br] {{ 'base.plan_fact' | translate }}
        th.table__cell(v-if="type === 'load'") {{ 'base.loading_time' | translate }}
        th.table__cell(v-else) {{ 'base.uploading_time' | translate }}
        th.table__cell {{ 'reports.errors_count' | translate }}
    v-table-body(
      :cols="type === 'load' ? 12 : 12"
      :empty="!rows.length"
      :loading="loading")
      tr.table__row.table__row--hover(
        v-for="(item, index) in rows"
        :key="item.id"
        @click="$emit('select', item)")
        td.table__cell.nowrap {{ item.planned_on }}
        td.table__cell.nowrap
          status-label(:status="item.status")
        td.table__cell.nowrap {{ shortStr(item.loading_name) }}
        td.table__cell.wrap-text {{ item.name }}
        td.table__cell.wrap-text {{ shortStr(item.ration_name) }}
        td.table__cell.wrap-text {{ shortStr(item.animal_group_label) }}
        td.table__cell.nowrap {{ item.animal_count }}
        td.table__cell.nowrap {{ item.mixer_balance }}
        td.table__cell.nowrap
          span.weight-badge(:class="getWeightColor(item, type)") {{ item.plan_weight }} / {{ item.fact_weight }}
        td.table__cell.nowrap {{ item.plan_per_cow }} / {{ item.fact_per_cow }}
        td.table__cell.nowrap {{ ( Math.round(item.mix_time/ 60))}} {{ 'base.min' | translate }}
        td.table__cell.nowrap
          span.error-badge(
            v-if="item.errors_count > 0"
            :class="{'error-red': item.errors_count > 0}") {{ item.errors_count }}
          span(v-else) -
</template>

<script>
import { formatDateUnified } from '@/util'
import { mapActions } from 'vuex'
import StatusLabel from './StatusLabel.vue'
import api from '@/api'

export default {
  name: 'GeneralTable',

  props: {
    loading: Boolean,
    type: {
      type: String,
      default: 'upload'
    },
    items: Array
  },

  components: {
    StatusLabel
  },

  data: () => ({
    rows: [],
    monitoringSettings: null,
    ingredientTolerances: [],
    groupTolerances: []
  }),

  async mounted () {
    if (!this.farmId) {
      return
    }

    try {
      // Load general monitoring settings
      const response = await api.monitoring.farms(this.farmId)
      this.monitoringSettings = response.data

      // Load individual ingredient tolerance settings
      try {
        const ingredientsResponse = await api.monitoring.ingredients({ farm: this.farmId })

        let ingredientsData = []
        if (ingredientsResponse.data.data && Array.isArray(ingredientsResponse.data.data)) {
          ingredientsData = ingredientsResponse.data.data
        } else if (ingredientsResponse.data.results && Array.isArray(ingredientsResponse.data.results)) {
          ingredientsData = ingredientsResponse.data.results
        } else if (Array.isArray(ingredientsResponse.data)) {
          ingredientsData = ingredientsResponse.data
        }

        this.ingredientTolerances = ingredientsData
      } catch (error) {
        this.ingredientTolerances = []
      }

      // Load individual group tolerance settings
      try {
        const groupsResponse = await api.monitoring.groups({ farm: this.farmId })

        let groupsData = []
        if (groupsResponse.data.data && Array.isArray(groupsResponse.data.data)) {
          groupsData = groupsResponse.data.data
        } else if (groupsResponse.data.results && Array.isArray(groupsResponse.data.results)) {
          groupsData = groupsResponse.data.results
        } else if (Array.isArray(groupsResponse.data)) {
          groupsData = groupsResponse.data
        }

        this.groupTolerances = groupsData
      } catch (error) {
        this.groupTolerances = []
      }
    } catch (error) {
      console.error('Error loading monitoring settings:', error)
    }

    this.$nextTick(async () => {
      if (this.$route.path.includes('farm')) {
        const farmId = this.$route.params.farm_id
        const { timezone } = await this.getFarm(farmId)
        this.$store.dispatch('account/setTimezone', timezone)
        this.formateDate()
      }
    })
  },

  computed: {
    currentLang () {
      return this.$store.getters['account/lang']
    },
    farmId () {
      return this.$route.params.farm_id
    },
    allowViewMonitoringFarm () {
      return this.hasPermission('monitoring_farm', 'view')
    }
  },

  methods: {
    ...mapActions('farms', [
      'getFarm'
    ]),

    getWeightColor (item, type) {
      if (!this.allowViewMonitoringFarm) {
        return ''
      }

      const isCompleted = type === 'load'
        ? (item.status === 'loaded' || item.status === 'uploaded')
        : item.status === 'uploaded'

      if (!isCompleted) {
        return ''
      }

      if (!this.monitoringSettings) {
        return ''
      }

      const settings = type === 'load'
        ? this.monitoringSettings.load_weight
        : this.monitoringSettings.upload_weight

      if (!settings || !settings.enabled) {
        return ''
      }

      const planWeight = item.plan_weight
      const factWeight = item.fact_weight

      if (!planWeight || planWeight === 0) {
        return ''
      }

      if (factWeight === null || factWeight === undefined) {
        return ''
      }

      const deviationPercent = ((factWeight - planWeight) / planWeight) * 100
      const absDeviation = Math.abs(deviationPercent)

      let threshold = null

      if (settings.level === 'percent') {
        threshold = settings.percent
      } else if (settings.level === 'weight' && planWeight > 0) {
        threshold = (settings.weight / planWeight) * 100
      }

      if (threshold !== null && threshold !== undefined && threshold !== 0) {
        if (absDeviation <= threshold) {
          return 'weight-green'
        }
        return 'weight-red'
      }

      if (settings.green !== 0 && absDeviation <= settings.green) {
        return 'weight-green'
      }

      if (settings.red !== 0 && absDeviation >= settings.red) {
        return 'weight-red'
      }

      if (settings.yellow !== 0 && absDeviation < settings.yellow) {
        return 'weight-yellow'
      }

      return ''
    },

    /**
     * Calculate error count for loading (ingredients)
     */
    calculateLoadErrors (element) {
      const isCompleted = element.status === 'loaded' || element.status === 'uploaded'
      if (!isCompleted) {
        return 0
      }

      const ingredients = element.schedule_ration.ingredients || []
      let errorsCount = 0

      ingredients.forEach(ingredient => {
        const planWeight = ingredient.plan_weight
        const factWeight = ingredient.fact_weight

        if (!planWeight || planWeight === 0) {
          return
        }

        if (factWeight === null || factWeight === undefined) {
          return
        }

        const ingredientId = ingredient.ingredient_id || ingredient.ingredient

        // Find individual tolerance settings for this ingredient
        const tolerance = this.ingredientTolerances.find(t => t.ingredient && t.ingredient.id === ingredientId)

        // Skip if no individual settings or if disabled
        if (!tolerance || tolerance.level === null) {
          return
        }

        // Calculate deviation
        const deviationPercent = ((factWeight - planWeight) / planWeight) * 100
        const absDeviation = Math.abs(deviationPercent)

        let hasError = false

        if (tolerance.level === 'percent') {
          if (absDeviation > tolerance.percent) {
            hasError = true
          }
        } else if (tolerance.level === 'weight') {
          const weightDeviationPercent = (tolerance.weight / planWeight) * 100
          if (absDeviation > weightDeviationPercent) {
            hasError = true
          }
        }

        if (hasError) {
          errorsCount++
        }
      })

      return errorsCount
    },

    /**
     * Calculate error count for uploading (groups)
     */
    calculateUploadErrors (element) {
      const isCompleted = element.status === 'uploaded'
      if (!isCompleted) {
        return 0
      }

      const groups = element.schedule_groups || []
      let errorsCount = 0

      groups.forEach(group => {
        const planWeight = group.plan_weight
        const factWeight = group.fact_weight

        if (!planWeight || planWeight === 0) {
          return
        }

        if (factWeight === null || factWeight === undefined) {
          return
        }

        const groupId = group.group

        // Find individual tolerance settings for this group
        const tolerance = this.groupTolerances.find(t => t.group && t.group.id === groupId)

        // Skip if no individual settings or if disabled
        if (!tolerance || tolerance.level === null) {
          return
        }

        // Calculate deviation
        const deviationPercent = ((factWeight - planWeight) / planWeight) * 100
        const absDeviation = Math.abs(deviationPercent)

        let hasError = false

        if (tolerance.level === 'percent') {
          if (absDeviation > tolerance.percent) {
            hasError = true
          }
        } else if (tolerance.level === 'weight') {
          const weightDeviationPercent = (tolerance.weight / planWeight) * 100
          if (absDeviation > weightDeviationPercent) {
            hasError = true
          }
        }

        if (hasError) {
          errorsCount++
        }
      })

      return errorsCount
    },

    formateDate () {
      this.rows = []
      let useGroupsValues = this.$route.path.includes('mixer-uploads')
      this.items.forEach(element => {
        let item = {}
        item.id = element.id
        item.name = element.name
        item.average_auger_rotations = element.schedule_mixer.average_auger_rotations
        item.mixer_balance = element.mixer_balance
        item.planned_on = formatDateUnified(element.planned_on)
        item.status = element.status
        item.loading_name = element.schedule_loading.name
        item.ration_name = element.schedule_ration.name
        item.animal_count = element.schedule_groups_list.animal_count
        item.animal_type_label = this.getArrLabel(element.schedule_groups_list.animal_types)
        item.animal_group_label = this.getArrLabel(element.schedule_groups_list.names)

        if (useGroupsValues) {
          item.plan_weight = +this.getArrCount(element.schedule_groups, 'plan_weight').toFixed(0)
          item.fact_weight = +this.getArrCount(element.schedule_groups, 'fact_weight').toFixed(0)
        } else {
          item.plan_weight = +this.getArrCount(element.schedule_ration.ingredients, 'plan_weight').toFixed(0)
          item.fact_weight = +this.getArrCount(element.schedule_ration.ingredients, 'fact_weight').toFixed(0)
        }

        item.plan_per_cow = (item.plan_weight / item.animal_count).toFixed(1)
        item.fact_per_cow = (item.fact_weight / item.animal_count).toFixed(1)

        if (this.type === 'load') {
          item.mix_time = this.getArrCount(element.schedule_ration.ingredients, 'time_load')
          item.errors_count = this.calculateLoadErrors(element)
        } else {
          item.mix_time = this.getArrCount(element.schedule_groups, 'time_load')
          item.errors_count = this.calculateUploadErrors(element)
        }

        this.rows.push(item)
      })
    },

    getArrLabel (array) {
      let label = ''
      array.forEach((element, index) => {
        label += element
        if (index < array.length - 1) {
          label += ', '
        }
      })
      return label
    },

    getArrCount (array, field) {
      let count = 0
      array.forEach(element => {
        count += element[field] ? element[field] : 0
      })
      return count
    },

    shortStr (str) {
      return str.length > 10 ? (str.substring(0, 10) + '...') : str
    }
  },

  watch: {
    items (v) {
      this.formateDate()
    },
    currentLang: {
      handler () {
        this.formateDate()
      }
    }
  }
}
</script>

<style scoped>
.table--bars tr:not(.table__row--head)>.table__cell {
  padding: 1.1rem 1rem;
}

.table__cell {
  padding: 1.8rem 1rem;
  font-size: 14px;
}

.table__cell.wrap-text {
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 200px;
  min-width: 100px;
}

.weight-badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-weight: 500;
  min-width: 6rem;
  text-align: center;
}

.weight-green {
  background-color: #91EA91;
}

.weight-yellow {
  background-color: #FBE268;
}

.weight-red {
  background-color: #FF9191;
}

.error-badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-weight: 500;
  min-width: 3rem;
  text-align: center;
}

.error-red {
  background-color: #FF9191;
  color: #000;
}
</style>